/*
================================================================================
                    WPQ TUBESHEET TRANSACTION - BUSINESS LOGIC
================================================================================

PURPOSE: Specialized 4-tab WPQ workflow for tube-to-tubesheet welding

KEY FEATURES:
• GTAW process focus (default for tubesheet applications)
• 4-tab workflow: General → Parameters → Tests → Certification
• Complex test structures: Visual, Liquid Penetrant, Macro, Leak Path, Radiography
• Tube location tracking (LocA/B/C/D) for test correlation
• OTV (Orbital Tube Welding) parameters
• Post-weld heat treatment specifications

TEST OBJECTS:
• Visual/Liquid Penetrant: Weld pass tracking
• Macro/Leak Path: Table1/Table2 structures with tube locations
• Radiography: Tube number correlation
• Other: Custom test specifications

BUSINESS RULES:
• 178-day validity from wld_createdon
• Tube location tracking mandatory
• Complex test array validation required
• All 4 tabs must be completed

================================================================================
*/

import { useState, useRef, useEffect } from "react";
import { Tabs, Tab } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import { getbyrecordno } from "../../Services/wpq_groove-services";
import { WPQHeader_Tube } from "./WPQObjects/WPQ_entity";
import { Action, Status, MessageTypes } from "../utility/common/constants/CommonConstants"
import { getdetailsByIdTube } from "../../Services/wpq_details-service";
import WPQTubesheetPage1 from "./WPQTubesheetPage1";
import WPQ_Tubesheet_Page2 from "./WPQTubesheetPage2";
import WPQ_Tubesheet_Page3 from "./WPQTubesheetPage3";
import WPQ_Tubesheet_Page4 from "./WPQTubesheetPage4";
import { GridLoader } from "react-spinners";
import { bluestyle, errorstyle, normalstyle } from "../../functions/welding-functions";
import { DisableWPQFields, addWeldingValidUptoDate } from "./WPQObjects/WPQCommonUtils";
import { NavigationButtons } from "../utility/ui/navigation/TransactionNavigation/NavigationButtons";
import { showToast, executeChildFunction, handleNavigation } from "../utility/ui/navigation/TransactionNavigation/TransactionUtils";


// Test object creation utilities
const TEST_TEMPLATES = {
  VISUAL_EXAMINATION: {
    WPQ_VEX_WeldPass: "",
    WPQ_VEX_Acceptance_Criteria: "",
    WPQ_VEX_Observation: "",
    WPQ_VEX_Results: ""
  },
  MACRO_EXAMINATION_5: {
    WPQ_MEX_Magnification: "",
    WPQ_MEX_Acceptance_Criteria: ""
  },
  MINIMUM_LEAK_PATH_5: {
    WPQ_MLP_Magnification: "",
    WPQ_MLP_Acceptance_Criteria: ""
  },
  LIQUID_PENETRANT_EXAMINATION: {
    WPQ_LPEX_WeldPass: "",
    WPQ_LPEX_Acceptance_Criteria: "",
    WPQ_LPEX_Observation: "",
    WPQ_LPEX_Results: ""
  },
  OTHER_TEST: {
    WPQ_OT_TypeofTest: "",
    WPQ_OT_Acceptance_Criteria: "",
    WPQ_OT_Observation: "",
    WPQ_OT_Results: ""
  },
  RADIOGRAPHY_TEST: {
    WPQ_Radio_TubeNo: "",
    WPQ_Radio_Acceptance_Criteria: "",
    WPQ_Radio_Observation: "",
    WPQ_Radio_Results: ""
  },
  MACRO_EXAMINATION: {
    WPQ_MEX_Tube_No: "",
    WPQ_MEX_LocA: "",
    WPQ_MEX_LocB: "",
    WPQ_MEX_LocC: "",
    WPQ_MEX_LocD: ""
  },
  MINIMUM_LEAK_PATH: {
    WPQ_MLP_Tube_No: "",
    WPQ_MLP_LocA: "",
    WPQ_MLP_LocB: "",
    WPQ_MLP_LocC: "",
    WPQ_MLP_LocD: ""
  }
};

const currentDate = new Date();
const year = currentDate.getFullYear();
const month = String(currentDate.getMonth() + 1).padStart(2, "0");
const day = String(currentDate.getDate()).padStart(2, "0");
const formattedDate = `${day}-${month}-${year}`;

const lastWeldDate = new Date(
  currentDate.getTime() + addWeldingValidUptoDate()
);

const year1 = lastWeldDate.getFullYear();
const month1 = String(lastWeldDate.getMonth() + 1).padStart(2, '0');
const day1 = String(lastWeldDate.getDate()).padStart(2, '0');
const WPQ_Welder_ExpiresOn = `${year1}-${month1}-${day1}`;


const createTestObjectArray = (count, template) => {
  return Array.from({ length: count }, () => ({ ...template }));
};

const createComplexTableStructure = (count, template) => {
  return [{
    Table1: Array.from({ length: count }, () => ({ ...template })),
    Table2: Array.from({ length: count }, () => ({ ...template }))
  }];
};
/*
 * WPQTubeSheetTransaction Component - Specialized tubesheet qualification handler
 * 
 * COMPONENT WORKFLOW:
 * 1. Initialize tubesheet-specific data structures
 * 2. Create complex test object arrays for comprehensive testing
 * 3. Handle 4-tab navigation with specialized validation
 * 4. Manage tube location and test correlation data
 * 5. Process GTAW-specific welding parameters
 * 6. Handle specialized approval workflow for tubesheet applications
 */
const WPQTubeSheetTransaction = () => {
  const [key, setKey] = useState(1);
  const firstChildRef = useRef();
  const secondChildRef = useRef();
  const thirdChildRef = useRef();
  const fourthChildRef = useRef();
  const location = useLocation();
  let UserName = sessionStorage.getItem("UserName");
  let UserId = sessionStorage.getItem("UserId");
  const [copydata, setcopydata] = useState("");
  const originalaction = location.state?.action;
  const WPStype = location.state?.WPStype;
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(location.state?.action);
  const [headerObj, setheaderObj] = useState(
    action === Action.ActionEdit || action === Action.ActionCopy
      ? location.state?.data
      : WPQHeader_Tube(WPStype)
  );


  const [isReadonly, setReadonly] = useState(false);
  const [isDisabled, setisDisabled] = useState(false)

  const [detailObj, setdetailObj] = useState(
    action === Action.ActionEdit || action === Action.ActionCopy
      ? location.state?.data
      : {
        SK: "",
        PK: "",
        WPQ_WelderId: "",
        WPQ_WelderName: "",
        WPQ_RecordNo: "",
        WPSH_Project_No: "",
        WPSH_WPS_No: "",
        WPQ_Process: "GTAW",
        WPSD_FM_SFA: "",
        WPSD_FM_Classification: "",
        WPSD_FM_FNo: "",
        WPSD_FM_Ano: "",
        WPSD_FM_ChemicalAnalysis_TradeName: "",
        wld_createdby: "",
        wld_createdon: formattedDate,
        WPQ_Welder_ExpiresOn: WPQ_Welder_ExpiresOn,
        wld_updatedby: "",
        wld_updatedon: "",
        WPQD_WP_WeldingProcess: "GTAW",
        WPQD_WP_Type: "",

        WPQD_WP_MinimumPreheatTemp: "",
        WPQD_WP_MaximumInterpassTemp: "",
        WPQD_WP_PostHeating: "",
        WPQD_WP_TungstenType: "",
        WPQD_WP_WeldPosition: "",
        WPQD_WP_WeldProgression: "",
        WPQD_WP_NoofPasses: "",

        WPQD_WP_LayerNo: [{ Value_A: [""] }, { Value_B: [""] }],
        WPQD_WP_Filler_Metal_Size: [{ Value_A: [""] }, { Value_B: [""] }],

        WPQD_WP_Amperes: [{ Value_A: [""] }, { Value_B: [""] }],
        WPQD_WP_DC_Pulsing_current: [{ Value_A: [""] }, { Value_B: [""] }],
        WPQD_WP_DC_Pulsing_current_Others: [
          { Value_A: [""] },
          { Value_B: [""] },
        ],
        WPQD_WP_Volatage_Volts: [{ Value_A: [""] }, { Value_B: [""] }],
        WPQD_WP_Travel_Speed: [{ Value_A: [""] }, { Value_B: [""] }],
        WPQD_WP_Heat_Input: [{ Value_A: [""] }, { Value_B: [""] }],
        WPQD_WP_PolarityOrCurrent: [{ Value_A: [""] }, { Value_B: [""] }],

        WPQD_WP_ShieldingGasType: "",
        WPQD_WP_ShieldingFlowRate: "",

        WPQD_WP_BackingGasType: "",
        WPQD_WP_BackingFlowRate: "",

        WPQD_WP_Auxiliarygas: "",

        WPQD_WP_Automatic_Arc_Voltage: "",

        WPQ_OTV_Expansion: "",
        WPQ_OTV_StringWave: "",
        WPQ_OTV_MethodofCleaning: "",
        WPQ_OTV_OrifceCupSize: "",
        WPQ_OTV_SingleMultiElectrode: "",
        WPQ_OTV_Oscillation: "",
        WPQ_OTV_Peening: "",
        WPQ_OTV_ClosedtooutChamber: "",
        WPQ_OTV_UseOfThermalProcess: "",
        WPQ_OTV_Others: "",

        WPQ_Post_WHT_Temp: "",
        WPQ_Post_WHT_Time: "",
        WPQ_Post_WHT_Type: "",
        WPQ_Post_WHT_Heating_Rate: "",
        WPQ_Post_WHT_Cooling_Rate: "",

        WPQ_Page2_Notes: "",

        WPQ_Certification_Welder_Name: "",
        WPQ_Certification_ID: "",
        WPQ_Certification_ConductedBy: "",
        WPQ_Certification_Mechanical_TestsBy: "",
        WPQ_Certification_Lab_RefNo: "",
        WPQ_VEX_Report: "",
        WPQ_LPEX_Report: "",
        WPQ_LPEX_Notes: "",
        WPQ_VEX_Notes: "",
        WPQ_MEX_Result: "",

        WPQ_Radio_Notes: "",
        WPQ_OT_Notes: "",

        WPQ_MLP_Result: "",
        WPQ_MEX_Observation: "",
        WPQ_MLP_Observation: "",
      }
  );

  const [testObj, setTestObj] = useState([]);

  useEffect(() => {
    if (action === Action.ActionAdd) {
      let Visual_Examination = Visual_Examination_Obj(1);
      let Liquid_Penetrant_Examination = Liquid_Penetrant_Examination_Obj(1);
      let Macro_Examination = Macro_Examination_Obj(1);
      let Macro_Examination5 = Macro_Examination5_Obj(1);
      let Other_Test = Other_Test_Obj(1);
      let Radiography_Test = Radiography_Test_Obj(1);
      let Minimum_Leak_Path = Minimum_Leak_Path_Obj(1);
      let Minimum_Leak_Path5 = Minimum_Leak_Path5_Obj(1);

      setdetailObj({
        ...detailObj,
        Visual_Examination,
        Liquid_Penetrant_Examination,
        Macro_Examination,
        Macro_Examination5,
        Other_Test,
        Radiography_Test,
        Minimum_Leak_Path,
        Minimum_Leak_Path5,
      });
    }
  }, []);

  // Creates specialized test arrays for tubesheet examinations
  function Visual_Examination_Obj(start) {
    return createTestObjectArray(2, TEST_TEMPLATES.VISUAL_EXAMINATION);
  }

  function Macro_Examination5_Obj(start) {
    return createTestObjectArray(3, TEST_TEMPLATES.MACRO_EXAMINATION_5);
  }
  function Minimum_Leak_Path5_Obj(start) {
    return createTestObjectArray(3, TEST_TEMPLATES.MINIMUM_LEAK_PATH_5);
  }

  function Liquid_Penetrant_Examination_Obj(start) {
    return createTestObjectArray(2, TEST_TEMPLATES.LIQUID_PENETRANT_EXAMINATION);
  }

  function Other_Test_Obj(start) {
    return createTestObjectArray(4, TEST_TEMPLATES.OTHER_TEST);
  }

  function Radiography_Test_Obj(start) {
    return createTestObjectArray(4, TEST_TEMPLATES.RADIOGRAPHY_TEST);
  }

  function Macro_Examination_Obj(start) {
    return createComplexTableStructure(5, TEST_TEMPLATES.MACRO_EXAMINATION);
  }

  function Minimum_Leak_Path_Obj(start) {
    return createComplexTableStructure(5, TEST_TEMPLATES.MINIMUM_LEAK_PATH);
  }


  const [logObj, setlogObj] = useState({
    WPSH_Project_No_log:
      headerObj != undefined ? headerObj.WPSH_Project_No : "",
    WPSH_WPS_No_log: headerObj != undefined ? headerObj.WPSH_WPS_No : "",
    WPSH_User_Name_log: UserName,
    WPSH_User_Id_log: UserId,
    WPSH_Status_log: "",
  });

  // Fetches tubesheet header and detail records
  const fetchprojectbyid = async (IdprojectPK, IdprojectSK) => {
    const result = await getbyrecordno(WPStype, IdprojectPK, IdprojectSK);
    setheaderObj(result[0]);
  };

  const fetchDetailsByWelderIdWpdNo = async (IdprojectPK, IdprojectSK, IdPK, IdSK) => {
    const result = await getdetailsByIdTube(IdprojectPK, IdprojectSK, IdPK, IdSK);
    if(result?.length > 0){
      if (action === Action.ActionCopy) {
        //if (item?.WI_WPQ_SlipGenerationStatus === "Yes") {
        result[0].WI_WPQ_SlipGenerationStatus = "No";
        //}
        // Clear all document/file related fields
        const [wpqDtYear, wpqDtMonth, wpqDtDay] = result[0].WPQ_Date.split("-").map(String);
        result[0].wld_createdon = `${wpqDtDay}-${wpqDtMonth}-${wpqDtYear}`;;
      }
      // Recalculate real-time active/inactive status based on expiry
      if (result[0].WPQ_Welder_ExpiresOn) {
        const expiryDate = new Date(result[0].WPQ_Welder_ExpiresOn);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        result[0].wld_process_status = expiryDate >= today ? "Active" : "Inactive";
      }
    }
    setdetailObj(result[0]);
  };

  useEffect(() => {
    const loadData = async () => {
      if (action !== Action.ActionAdd) {
        let IdprojectPK = location.state?.data.WPQ_WelderId;
        let IdprojectSK = location.state?.data.WPQ_RecordNo;

        let IdPK = location.state?.data.WPSH_WPQ_Process;
        let IdSK = location.state?.data.WPSH_WPQ_Type;
        await fetchprojectbyid(IdprojectPK, IdprojectSK);
        await fetchDetailsByWelderIdWpdNo(IdprojectPK, IdprojectSK, IdPK, IdSK);
      }
      setLoading(false);

      if (action === Action.ActionCopy) {
        setcopydata(true);
      }
    };
    loadData();
  }, []);

  const next = async (e) => {
    const refs = [firstChildRef, secondChildRef, thirdChildRef, fourthChildRef];
    const funcNames = ['funcOnNext', 'funcOnNext2', 'funcOnNext3', 'funcOnNext4'];
    const funcName = funcNames[key - 1];
    const res = await executeChildFunction(refs, key, funcName);
    showToast(res, key, setKey, 4, true);
    if (res === MessageTypes.Success) {
      setAction(Action.ActionEdit);
    }
  };

  const handleSelect = (e) => {
    Temp(e, "tab");
  };

  const Temp = async (e, tabaction) => {
    const refs = [firstChildRef, secondChildRef, thirdChildRef, fourthChildRef];
    const funcNames = ['funcOnNextTemp', 'funcOnNextTemp2', 'funcOnNextTemp3', 'funcOnNextTemp4'];
    const funcName = funcNames[key - 1];
    const res = await executeChildFunction(refs, key, funcName);

    showToast(res, key, setKey, 4, true);

    if (res === MessageTypes.Temp) {
      setAction(Action.ActionEdit);
    }

    if (res === MessageTypes.Temp || res === MessageTypes.None) {
      handleNavigation(tabaction, e, key, setKey, 4);
    }
  };

  useEffect(() => {
    let disableFieldFlg = DisableWPQFields(headerObj.WPQH_Status, headerObj.WPQH_PreparedBy, Status, Action, action);
    setisDisabled(disableFieldFlg);
    setReadonly(disableFieldFlg);
  }, [action]);

  return (
    <>
      {loading && (
        <div className="loader-overlay">
          <GridLoader size={10} className="pageloader" />
        </div>
      )}
      <div className="position-absolute ms-3" style={{ top: "15px" }}>
        <strong>Add New</strong>
        <span className="ms-1 warning-text" style={{ fontSize: "small" }}>
          (All fields are mandatory)
        </span>
      </div>
      <div className="border-divider" />
      <div className="mt-4">
        <div style={{ borderBottom: "2px solid var(--col-ddd)", position: "relative", top: "34px", zIndex: "-2" }}></div>
        <Tabs
          activeKey={key}
          onSelect={(e) => handleSelect(e)}
          className="mb-2 w-100 border-end"
          fill
        >
          <Tab
            eventKey={1}
            title="WPQ Page 1(WPQ Tube to Tubesheet)"
            style={{
              height: window.innerHeight - 180,
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            <WPQTubesheetPage1
              ref={firstChildRef}
              headerObj={headerObj}
              setheaderObj={setheaderObj}
              detailObj={detailObj}
              setdetailObj={setdetailObj}
              normalstyle={normalstyle}
              errorstyle={errorstyle}
              bluestyle={bluestyle}
              WPStype={WPStype}
              setKey={setKey}
              tabno={key}
              isReadonly={isReadonly}
              setReadonly={setReadonly}
              action={action}
              setAction={setAction}
            />

          </Tab>
          <Tab
            eventKey={2}
            title="WPQ Page 2"
            style={{
              height: window.innerHeight - 180,
              overflowY: "auto",
              overflowX: "hidden",
            }}
            disabled={action == Action.ActionAdd}

          // disabled={action == Action.ActionAdd ? true : false}
          >
            <WPQ_Tubesheet_Page2
              ref={secondChildRef}
              headerObj={headerObj}
              setheaderObj={setheaderObj}
              detailObj={detailObj}
              setdetailObj={setdetailObj}
              copydata={copydata}
              WPStype={WPStype}
              logObj={logObj}
              setKey={setKey}
              tabno={key}
              setlogObj={setlogObj}
              isDisabled={isDisabled}
              isReadonly={isReadonly}
              setReadonly={setReadonly}
              action={action}
              setAction={setAction}
            />
          </Tab>
          <Tab
            eventKey={3}
            title="WPQ Page 3"
            style={{
              height: window.innerHeight - 180,
              overflowY: "auto",
              overflowX: "hidden",
            }}
            disabled={action == Action.ActionAdd}
          >
            <WPQ_Tubesheet_Page3
              ref={thirdChildRef}
              headerObj={headerObj}
              setheaderObj={setheaderObj}
              detailObj={detailObj}
              setdetailObj={setdetailObj}
              testObj={testObj}
              setTestObj={setTestObj}
              copydata={copydata}
              WPStype={WPStype}
              logObj={logObj}
              setKey={setKey}
              tabno={key}
              setlogObj={setlogObj}
              isDisabled={isDisabled}
              isReadonly={isReadonly}
              setReadonly={setReadonly}
              action={action}
              setAction={setAction}

            />
          </Tab>
          <Tab
            eventKey={4}
            title="WPQ Page 4"
            style={{
              height: window.innerHeight - 180,
              overflowY: "auto",
              overflowX: "hidden",
            }}
            disabled={action == Action.ActionAdd}

          // disabled={action == Action.ActionAdd ? true : false}
          >
            <WPQ_Tubesheet_Page4
              ref={fourthChildRef}
              headerObj={headerObj}
              setheaderObj={setheaderObj}
              detailObj={detailObj}
              setdetailObj={setdetailObj}
              testObj={testObj}
              setTestObj={setTestObj}
              copydata={copydata}
              WPStype={WPStype}
              logObj={logObj}
              setKey={setKey}
              tabno={key}
              setlogObj={setlogObj}
              isDisabled={isDisabled}
              isReadonly={isReadonly}
              setReadonly={setReadonly}
              action={action}
              setAction={setAction}

            />
          </Tab>
        </Tabs>

        <NavigationButtons
          currentKey={key}
          maxTabs={4}
          isBtnDisabled={loading}
          originalAction={originalaction}
          onNext={next}
          onTemp={Temp}
        />
      </div>
    </>
  );
};

export default WPQTubeSheetTransaction;
